import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, ShieldCheck, X } from 'lucide-react'; 

// Refers to the API located in src/components/services/
import { getMarketQuote } from './services/avaAPI'; 

/**
 * STRATEGIC ANALYSIS MODAL
 * Purpose: Conversion of high-intent visitors into Qualified Leads.
 * Theme: Forensic / Institutional Data Analysis.
 */
const StrategicAnalysisModal = ({ isOpen, onClose, handleUserChoice, studiedContext, sessionId }) => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [complexity, setComplexity] = useState("consult"); 
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const isMobile = typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const getScopeLabel = useCallback(() => {
    switch (complexity) {
      case 'envelope': return "Structural Squares (Auditoría de Envolvente)"; 
      case 'commission': return "System Nodes (Supervisión)"; 
      case 'engineering': return "Infrastructure Points (Revisión Lógica)"; 
      case 'design': return "Project Zones (Alcances de Diseño)"; 
      case 'consult': return "Diagnostic Evaluation (Horas de Consultoría)"; 
      default: return "Analysis Scope (Alcance de Análisis)";
    }
  }, [complexity]);

  const fetchQuote = useCallback(async (qty, tier) => {
    const category = studiedContext || 'Strategic Consultation';
    setIsCalculating(true);
    try {
      const data = await getMarketQuote({ 
        service: category, 
        quantity: qty,
        complexity: tier,
        sessionId: sessionId,
        deviceType: isMobile ? 'mobile' : 'desktop'
      });
      setQuote(data);
    } catch (err) {
      // Fallback rate logic for Charlotte Metro 2026
      const baseRate = tier === 'envelope' ? 750 : 450; 
      setQuote({ success: true, estimate: baseRate * qty, savings: 125 * qty, source: 'historical' });
    } finally {
      setIsCalculating(false);
    }
  }, [studiedContext, isMobile, sessionId]);

  useEffect(() => {
    if (quantity > 0 && isOpen) {
      const timer = setTimeout(() => fetchQuote(quantity, complexity), 400);
      return () => clearTimeout(timer);
    }
  }, [quantity, complexity, isOpen, fetchQuote]);

  useEffect(() => {
    const context = studiedContext?.toLowerCase() || "";
    if (context.includes('roofing') || context.includes('envelope')) setComplexity('envelope');
    else if (context.includes('installation') || context.includes('lutron')) setComplexity('commission');
    else if (context.includes('diagnostic') || context.includes('safety')) setComplexity('consult');
    else if (context.includes('infrastructure') || context.includes('panel')) setComplexity('engineering');
    else setComplexity('design');
  }, [studiedContext]);

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (email.includes("@")) {
      sessionStorage.setItem('leadEmail', email);
      const quoteVal = quote?.estimate ? `$${quote.estimate.toLocaleString()}` : 'N/A';
      handleUserChoice(`CONSULT_INTAKE: ${email} | Tier: ${complexity} | Scope: ${quantity} | VAL: ${quoteVal}`);
      setIsSubmitted(true);
    }
  };

  return (
    <>
      <style>{`
        .gold-shimmer { background: linear-gradient(to right, #1c1917 0%, #44403c 50%, #1c1917 100%); background-size: 200% auto; animation: shimmer 4s infinite linear; }
        @keyframes shimmer { to { background-position: 200% center; } }
      `}</style>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ y: "100%", opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: "100%", opacity: 0 }}
            className="fixed bottom-0 right-0 w-full sm:bottom-8 sm:right-8 sm:w-[400px] bg-white rounded-t-sm sm:rounded-sm shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] z-[10000] border border-stone-200 overflow-hidden font-sans"
          >
            <div className="bg-stone-900 p-5 flex justify-between items-center border-b border-amber-900/20">
              <div className="flex items-center gap-3">
                <BarChart3 size={14} className={isCalculating ? "text-amber-500 animate-pulse" : "text-amber-500"} />
                <h4 className="text-[9px] font-black text-white uppercase tracking-[0.4em]">Market Engineering Profile</h4>
              </div>
              <button onClick={onClose} className="text-stone-500 hover:text-white transition-colors">
                <X size={14} />
              </button>
            </div>
            
            <div className="p-8">
              <div className="bg-stone-50 p-6 border border-stone-100 rounded-sm mb-6">
                <select 
                  value={complexity} 
                  onChange={(e) => setComplexity(e.target.value)} 
                  className="w-full bg-transparent border-b border-stone-200 py-2 text-[10px] font-bold mb-6 outline-none text-stone-900 uppercase tracking-widest"
                >
                  <option value="consult">Principal Diagnostic Evaluation</option>
                  <option value="design">System Logic & Design Scope</option>
                  <option value="engineering">Infrastructure Lifecycle Review</option>
                  <option value="commission">Quality Assurance Oversight</option>
                  <option value="envelope">Structural Envelope Audit</option>
                </select>
                
                <label className="text-[8px] font-black text-stone-400 uppercase mb-2 block tracking-[0.3em]">
                    {getScopeLabel()}
                </label>
                
                <input 
                  type="number" 
                  value={quantity} 
                  onChange={(e) => setQuantity(Number(e.target.value))} 
                  className="w-full bg-transparent border-b border-stone-200 py-2 font-serif italic text-2xl text-stone-900 outline-none" 
                />
                
                {quote && (
                  <div className="mt-8 flex justify-between items-end">
                    <div className={isCalculating ? "opacity-40 transition-opacity" : "opacity-100 transition-opacity"}>
                      <p className="text-[8px] text-stone-400 font-bold uppercase tracking-[0.3em] mb-1">Target Capitalization</p>
                      <span className="text-3xl font-serif italic text-stone-900">${quote.estimate.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>

              {!isSubmitted ? (
                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <input 
                    required 
                    type="email" 
                    placeholder="Principal Email..." 
                    className="w-full p-4 border border-stone-200 rounded-sm text-xs outline-none focus:border-amber-900 transition-colors" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                  />
                  <button type="submit" className="gold-shimmer w-full p-5 text-white rounded-sm font-black text-[9px] uppercase tracking-[0.4em] hover:opacity-90 transition-opacity">
                    Secure Strategic Profile
                  </button>
                </form>
              ) : (
                <div className="bg-stone-900 p-8 rounded-sm text-center">
                  <ShieldCheck className="text-amber-500 mx-auto mb-4" size={32} />
                  <p className="text-[9px] font-black text-white uppercase tracking-[0.3em]">Protocolo Iniciado</p>
                </div>
              )}
              
              <button onClick={onClose} className="w-full text-stone-400 text-[8px] uppercase font-black mt-8 tracking-[0.3em] hover:text-stone-600 transition-colors">
                Exit Analysis Tool
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StrategicAnalysisModal;